import { format, parseISO } from "date-fns";
import styled from "@emotion/styled";
import { Typography } from "@avenue-8/sidekick-ui";
import { FormattedDate } from "@shared/components/FormattedDate";
import { LeadChip } from "@shared/components/LeadChip";
import type { BuyOrRentLeadInterestResponseDto } from "@shared/generated/crm/models/BuyOrRentLeadInterestResponseDto";
import type { LeadResponseDto } from "@shared/generated/crm/models/LeadResponseDto";
import type { SellOrRentOutLeadInterestResponseDto } from "@shared/generated/crm/models/SellOrRentOutLeadInterestResponseDto";
import { capitalize } from "@shared/utils/text-formatter";
import {
  INTENT_LABELS,
  formatBudget,
  buildPreferenceLines,
  buildPropertyLines,
} from "../lead-formatters";
import { LEAD_SIGNAL_MAPPERS } from "../signal-mappers";
import { ContentItem, ItemsStack } from "./SidekickUpdatesCard";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 1.25rem;

  &:first-of-type {
    padding-top: 0.25rem;
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ScheduleItemRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ScheduleMeta = styled(Typography)`
  color: ${(p) => p.theme.text.colors.secondary};
`;

type Props = {
  lead: LeadResponseDto;
  contactName: string;
};

const isBuyOrRent = (
  interest:
    | BuyOrRentLeadInterestResponseDto
    | SellOrRentOutLeadInterestResponseDto,
): interest is BuyOrRentLeadInterestResponseDto =>
  interest.type === "BUY" || interest.type === "RENT";

const isSellOrRentOut = (
  interest:
    | BuyOrRentLeadInterestResponseDto
    | SellOrRentOutLeadInterestResponseDto,
): interest is SellOrRentOutLeadInterestResponseDto =>
  interest.type === "SELL" || interest.type === "RENT_OUT";

const formatAppointmentType = (type: string) => type.replace(/_/g, " ");

export const LeadDetailsContent = ({ lead, contactName }: Props) => {
  const intentLabel = INTENT_LABELS[lead.intent.toUpperCase()] ?? lead.intent;

  const signals = (lead.leadSignals ?? [])
    .filter(
      (s) => s.status !== "superseded" && s.type !== "NO_INTEREST_EXPRESSED",
    )
    .sort(
      (a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    )
    .map((s) => LEAD_SIGNAL_MAPPERS[s.type](s));

  const buyerInterests = (lead.interests ?? []).filter(isBuyOrRent);
  const sellerInterests = (lead.interests ?? []).filter(isSellOrRentOut);

  const budgets = buyerInterests
    .flatMap((i) => {
      if (i.type !== "BUY" || !i.budget) return [];
      const formatted = formatBudget(i.budget);
      return formatted !== null ? [formatted] : [];
    });

  const allPreferences = buyerInterests.flatMap((i) =>
    i.propertyNeeds ? buildPreferenceLines(i.propertyNeeds) : [],
  );

  const allLocations = buyerInterests.flatMap((i) => i.locations ?? []);

  const allPropertyLines = sellerInterests.flatMap((i) =>
    i.prospectProperty ? buildPropertyLines(i.prospectProperty) : [],
  );

  const potentialSchedules = (lead.potentialSchedules ?? []).filter(
    (s) => s.status !== "rejected",
  );

  return (
    <>
      <Section>
        <Typography variant="subtitle1">Overview</Typography>
        <ChipsRow>
          <LeadChip value={intentLabel} />
          <LeadChip label="Stage" value={capitalize(lead.stage)} />
          {lead.firstSignalAt && (
            <LeadChip label="First signal" value={lead.firstSignalAt} type="date" />
          )}
          {lead.lastSignalAt && (
            <LeadChip label="Last signal" value={lead.lastSignalAt} type="date" />
          )}
        </ChipsRow>
      </Section>

      {signals.length > 0 && (
        <Section>
          <Typography variant="subtitle1">Signals</Typography>
          <ItemsStack>
            {signals.map((item, i) => (
              <ContentItem key={i} item={item} />
            ))}
          </ItemsStack>
        </Section>
      )}

      {potentialSchedules.length > 0 && (
        <Section>
          <Typography variant="subtitle1">Potential meetings</Typography>
          <ItemsStack>
            {potentialSchedules.map((s, i) => (
              <ScheduleItemRow key={i}>
                <Typography>
                  {contactName} <strong>{s.status ?? "proposed"}</strong> a{" "}
                  {formatAppointmentType(s.appointmentType ?? "meeting")}
                </Typography>
                <ScheduleMeta variant="body2">
                  {s.suggestedDateText ??
                    (s.suggestedDate
                      ? `${format(parseISO(s.suggestedDate), "MMM d, yyyy")}${s.suggestedTime ? ` · ${s.suggestedTime}` : ""}`
                      : "No date suggested yet")}
                </ScheduleMeta>
                {s.locationOrAddress && (
                  <ScheduleMeta variant="body2">{s.locationOrAddress}</ScheduleMeta>
                )}
              </ScheduleItemRow>
            ))}
          </ItemsStack>
        </Section>
      )}

      {budgets.length > 0 && (
        <Section>
          <Typography variant="subtitle1">Budget</Typography>
          {budgets.map((budget, i) => (
            <Typography key={i}>{budget}</Typography>
          ))}
        </Section>
      )}

      {allLocations.length > 0 && (
        <Section>
          <Typography variant="subtitle1">Locations</Typography>
          <BulletList>
            {allLocations.map((loc, i) => (
              <li key={i}>
                <Typography>{loc}</Typography>
              </li>
            ))}
          </BulletList>
        </Section>
      )}

      {allPreferences.length > 0 && (
        <Section>
          <Typography variant="subtitle1">Preferences</Typography>
          <BulletList>
            {allPreferences.map((pref, i) => (
              <li key={i}>
                <Typography>{pref}</Typography>
              </li>
            ))}
          </BulletList>
        </Section>
      )}

      {allPropertyLines.length > 0 && (
        <Section>
          <Typography variant="subtitle1">Property</Typography>
          <BulletList>
            {allPropertyLines.map((line, i) => (
              <li key={i}>
                <Typography>{line}</Typography>
              </li>
            ))}
          </BulletList>
        </Section>
      )}

      {lead.nextStep && (
        <Section>
          <Typography variant="subtitle1">Next step</Typography>
          <Typography>{lead.nextStep.description}</Typography>
          {lead.nextStep.dueAt && (
            <Typography>
              Due <FormattedDate value={lead.nextStep.dueAt} />
            </Typography>
          )}
        </Section>
      )}
    </>
  );
};
